import { useCallback } from 'react';

import qs from 'qs';

import { isChangeUrl } from '@change-corgi/core/navigation';

import { useQueryParamValue } from '#src/app/shared/hooks/location';

type Result = (options?: { additionalQueryParams?: Record<string, string> }) => string;

/**
 * Whether a `redirectTo`/`redirect_to` query param is actually present.
 *
 * Useful to distinguish "no redirect requested" from an explicit redirect that
 * happens to resolve to "/" (e.g. `redirect_to=/`) — `useGetRedirectUrl()`'s
 * return value alone can't tell those apart, since both normalize to "/".
 */
export function useHasRedirectUrlParam(): boolean {
	const redirectUrl1 = useQueryParamValue('redirectTo');
	const redirectUrl2 = useQueryParamValue('redirect_to');
	return redirectUrl1 !== undefined || redirectUrl2 !== undefined;
}

export function useGetRedirectUrl(): Result {
	const redirectUrl1 = useQueryParamValue('redirectTo');
	const redirectUrl2 = useQueryParamValue('redirect_to');
	const redirectUrl = redirectUrl1 || redirectUrl2 || '/';
	const safeRedirectUrl = isChangeUrl(redirectUrl) ? redirectUrl : '/';

	return useCallback(
		({ additionalQueryParams } = { additionalQueryParams: undefined }) => {
			const [path, search] = safeRedirectUrl.split('?');

			const query = qs.parse(search);

			// Remove tokens from the query params
			const filteredQueryParams = Object.fromEntries(
				Object.entries(query).filter(([key]) => key !== 'tk' && !key.endsWith('_tk')),
			);

			return `${path}${qs.stringify(
				{
					...filteredQueryParams,
					...(additionalQueryParams || {}),
				},
				{ addQueryPrefix: true },
			)}`;
		},
		[safeRedirectUrl],
	);
}
